import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';

const USE_TYPED = '__use_typed__';
const ALL_LOTES = '__all_lotes__';
const normalize = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
import {
  makeStyles, tokens, Spinner, Badge, Button,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Field, Input, Select, Textarea, Combobox, Option,
  
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { useAuthStore } from '../store/auth.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import {
  fetchLoteCycles, fetchLoteCropCycles, fetchPhenology, fetchPests, fetchPestAlerts, fetchScouting,
  createPhenologyRecord, createPestRecord,
  deletePhenologyRecord, deletePestRecord,
  type PhenologyRecord, type PestRecord, type ScoutingReport,
  type FenologiaStage, type PestType, type PestPressureLevel,
  type CreatePhenologyInput, type CreatePestInput,
} from '../api/crop.api';
import { ScoutingPhotoGallery } from '../components/ScoutingPhotoGallery';
import { orderedModules } from '../utils/module-tree';
import { CropAssignmentPanel } from '../components/crop/CropAssignmentPanel';
import { CropCatalogPanel } from '../components/crop/CropCatalogPanel';
import { fetchCrops, fetchModules } from '../api/terrain.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';
import { SubTabs } from '../components/shared/SubTabs';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  alerts:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

const STAGES: FenologiaStage[] = [
  'PREPARACION_SUELO', 'SIEMBRA', 'GERMINACION', 'EMERGENCIA', 'ESTABLECIMIENTO',
  'VEGETATIVO', 'FLORACION', 'FRUCTIFICACION', 'MADURACION', 'COSECHA', 'POST_COSECHA',
];
const PEST_TYPES: PestType[] = ['PLAGA', 'ENFERMEDAD', 'MALEZA'];
const PRESSURES: PestPressureLevel[] = ['BAJO', 'MEDIO', 'ALTO', 'CRITICO'];

function stageLabel(t: ReturnType<typeof useT>, stage: FenologiaStage): string {
  const key = `stage${stage}` as keyof ReturnType<typeof useT>['crop'];
  return String(t.crop[key] ?? stage);
}
function pressureLabel(t: ReturnType<typeof useT>, p: PestPressureLevel): string {
  const key = p === 'CRITICO' ? 'presureCRITICO' : (`pressure${p}` as keyof ReturnType<typeof useT>['crop']);
  return String(t.crop[key] ?? p);
}
function pestLabel(t: ReturnType<typeof useT>, p: PestType): string {
  const key = `pest${p}` as keyof ReturnType<typeof useT>['crop'];
  return String(t.crop[key] ?? p);
}

export function CropPage() {
  const t = useT();
  const s = useStyles();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const { formatDate } = useLocaleSettings();
  // Roles con permiso crop:write (ver DEFAULT_ROLE_PERMISSIONS en rbac.model.ts)
  const canWrite = user?.roles.some(r => ['AGRONOMIST', 'SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;

  const [loteId, setLoteId] = useState('');
  const [ciclo, setCiclo]   = useState('');           // valor confirmado
  const [cicloQuery, setCicloQuery] = useState('');  // texto que se esta escribiendo
  const [mainTab, setMainTab] = useState<'assignment' | 'catalog' | 'phenology' | 'scouting'>('assignment');
  const tab = mainTab;
  const [moduleId, setModuleId] = useState('');        // zona seleccionada ('' = todas)
  const [phenoDialog, setPhenoDialog] = useState(false);
  const [scoutDialog, setScoutDialog] = useState(false);
  const [phenoForm, setPhenoForm] = useState<Partial<CreatePhenologyInput>>({});
  const [scoutForm, setScoutForm] = useState<Partial<CreatePestInput>>({});
  const [formError, setFormError] = useState('');
  const fp = useFieldErrors();
  const fs = useFieldErrors();
  const errorText = (e: unknown, fallback: string): string =>
    (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => changeLote('') }, undefined, true);

  // Ciclos conocidos del lote (fenologia, riego, presupuestos, actividades).
  const { data: cycles = [] } = useQuery({
    queryKey: ['lote-cycles', loteId],
    queryFn:  () => fetchLoteCycles(loteId),
    enabled:  !!loteId,
  });
  const queryNorm = normalize(cicloQuery);
  const cicloOptions = cycles.filter(c => normalize(c.ciclo).includes(queryNorm));
  const exactMatch = cycles.find(c => normalize(c.ciclo) === queryNorm && queryNorm !== '');
  const showUseTyped = cicloQuery.trim() !== '' && !exactMatch;

  const confirmCiclo = (value: string) => {
    const v = value.trim();
    setCiclo(v);
    setCicloQuery(v);
  };
  const changeLote = (id: string) => {
    setLoteId(id);
    setModuleId('');
    setCiclo('');
    setCicloQuery('');
  };

  const enabled = !!loteId && !!ciclo;
  const selectedLote = lotes.find(l => l.loteId === loteId);
  const { data: cropsCatalog = [] } = useQuery({ queryKey: ['crops-catalog'], queryFn: fetchCrops });

  const { data: modules = [] } = useQuery({
    queryKey: ['modules-by-lote', loteId],
    queryFn:  () => fetchModules({ loteId }),
    enabled:  !!loteId,
  });
  const zoneName = (id?: string) => {
    const m = modules.find(x => x.moduleId === id);
    return m ? (m.name || m.moduleCode) : '—';
  };

  // Cultivo derivado del CICLO elegido: una zona (o el lote sin zonas) tiene cultivo si ese ciclo
  // existe como entidad (abierto o cerrado: captura tardía). El servidor deriva lo mismo.
  const { data: cropCycles = [] } = useQuery({
    queryKey: ['crop-cycles', loteId],
    queryFn:  () => fetchLoteCropCycles(loteId),
    enabled:  !!loteId,
  });
  const cropName = (id: string) => cropsCatalog.find(c => c.cropId === id)?.name ?? id;
  const candidates: { moduleId?: string; cropId: string; label: string }[] = cropCycles
    .filter(c => c.ciclo === ciclo && !!c.cropId)
    .filter(c => modules.length > 0 ? modules.some(m => m.moduleId === c.targetId) : c.targetId === loteId)
    .map(c => modules.length > 0
      ? { moduleId: c.targetId, cropId: c.cropId, label: `${zoneName(c.targetId)} — ${cropName(c.cropId)}` }
      : { cropId: c.cropId, label: cropName(c.cropId) });
  const scoped = moduleId ? candidates.filter(c => c.moduleId === moduleId) : candidates;
  const autoCrop = scoped.length === 1;
  const chosen = autoCrop ? scoped[0] : scoped.find(c => c.moduleId === phenoForm.moduleId);
  const chosenCropName = chosen ? (cropsCatalog.find(c => c.cropId === chosen.cropId)?.name ?? chosen.cropId) : '';
  const hasAssigned = scoped.length > 0;
  const payloadModuleId = chosen?.moduleId;

  const { data: phenology = [], isLoading: loadingPheno } = useQuery({
    queryKey: ['phenology', loteId, ciclo, moduleId],
    queryFn:  () => moduleId ? fetchPhenology(loteId, ciclo, moduleId) : fetchPhenology(loteId, ciclo),
    enabled,
  });
  const { data: pests = [], isLoading: loadingPests } = useQuery({
    queryKey: ['pests', loteId, ciclo, moduleId],
    queryFn:  () => moduleId ? fetchPests(loteId, ciclo, moduleId) : fetchPests(loteId, ciclo),
    enabled,
  });
  const { data: alerts = [] } = useQuery({
    queryKey: ['pest-alerts', loteId, ciclo],
    queryFn:  () => fetchPestAlerts(loteId, ciclo),
    enabled,
  });
  const { data: scoutingReports = [] } = useQuery({
    queryKey: ['scouting', loteId, ciclo],
    queryFn:  () => fetchScouting(loteId, ciclo),
    enabled,
  });

  const mutPheno = useMutation({
    mutationFn: (input: CreatePhenologyInput) => createPhenologyRecord(loteId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['phenology', loteId, ciclo] });
      setFormError('');
      setPhenoDialog(false);
      setPhenoForm({});
    },
    onError: (e) => setFormError(errorText(e, t.crop.assignError)),
  });
  const mutScout = useMutation({
    mutationFn: (input: CreatePestInput) => createPestRecord(loteId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pests', loteId, ciclo] });
      setFormError('');
      setScoutDialog(false);
      setScoutForm({});
    },
    onError: (e) => setFormError(errorText(e, t.crop.assignError)),
  });

  const deletePhenoMut = useMutation({
    mutationFn: (recordId: string) => deletePhenologyRecord(loteId, recordId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phenology', loteId, ciclo] }),
  });
  const deletePestMut = useMutation({
    mutationFn: (recordId: string) => deletePestRecord(loteId, recordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pests', loteId, ciclo] });
      qc.invalidateQueries({ queryKey: ['pest-alerts', loteId, ciclo] });
    },
  });

  return (
    <div className={s.root}>
      <PageTitle>{t.crop.title}</PageTitle>

      {mainTab !== 'catalog' && (
      <div className={s.filters}>
        <SearchableSelect
          label={t.crop.loteLabel}
          placeholder={t.common.searchLotePlaceholder}
          options={[{ id: ALL_LOTES, label: t.crop.loteAll }, ...lotes.map(l => ({ id: l.loteId, label: l.name }))]}
          value={loteId || ALL_LOTES}
          onChange={(id) => changeLote(id === ALL_LOTES ? '' : id)}
          noResultsText={t.common.searchNoResults}
        />
        {mainTab !== 'assignment' && (
        <Field label={t.crop.cicloLabel}>
          <Combobox
            freeform
            value={cicloQuery}
            selectedOptions={exactMatch ? [exactMatch.ciclo] : []}
            placeholder={t.crop.cicloPlaceholder}
            onInput={(e) => {
              const v = (e.target as HTMLInputElement).value;
              setCicloQuery(v);
              if (v.trim() === '') setCiclo('');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' && cicloQuery.trim()) confirmCiclo(exactMatch?.ciclo ?? cicloQuery); }}
            onBlur={() => { if (cicloQuery.trim() && cicloQuery.trim() !== ciclo) confirmCiclo(exactMatch?.ciclo ?? cicloQuery); }}
            onOptionSelect={(_, d) => {
              if (d.optionValue === undefined) return; // freeform: aviso al teclear
              if (d.optionValue === USE_TYPED) confirmCiclo(cicloQuery);
              else confirmCiclo(d.optionValue ?? '');
            }}
          >
            {cicloOptions.map(c => <Option key={c.ciclo} value={c.ciclo}>{c.ciclo}</Option>)}
            {showUseTyped && (
              <Option key={USE_TYPED} value={USE_TYPED} text={cicloQuery}>
                {t.crop.cicloUse.replace('{value}', cicloQuery.trim())}
              </Option>
            )}
          </Combobox>
        </Field>
        )}
      </div>
      )}

      <SubTabs selected={mainTab} onSelect={(v) => setMainTab(v as typeof mainTab)} tabs={[{ value: 'assignment', label: t.crop.tabAssignment }, { value: 'catalog', label: t.crop.tabCatalog }, { value: 'phenology', label: t.crop.tabPhenology }, { value: 'scouting', label: t.crop.tabScouting }]} />

      {mainTab === 'catalog' && <CropCatalogPanel canWrite={canWrite} />}

      {mainTab === 'assignment' && selectedLote && (
        <CropAssignmentPanel
          key={selectedLote.loteId} lote={selectedLote} canWrite={canWrite} ciclo={ciclo}
          onGoToCatalog={() => setMainTab('catalog')}
        />
      )}

      {mainTab === 'assignment' && !selectedLote && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL }}>
          {lotes.map(l => (
            <div key={l.loteId}>
              <h3 style={{ margin: '0 0 8px' }}>{l.name}</h3>
              <CropAssignmentPanel
                lote={l} canWrite={canWrite} ciclo={ciclo}
                onGoToCatalog={() => setMainTab('catalog')}
              />
            </div>
          ))}
        </div>
      )}

      {(mainTab === 'phenology' || mainTab === 'scouting') && !enabled && (
        <p style={{ color: tokens.colorNeutralForeground3 }}>{t.crop.noCiclo}</p>
      )}

      {(mainTab === 'phenology' || mainTab === 'scouting') && enabled && !hasAssigned && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t.crop.needAssignment}{' '}
            <Button size="small" appearance="transparent" onClick={() => setMainTab('assignment')}>{t.crop.needAssignmentGo}</Button>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Alertas activas */}
      {alerts.length > 0 && enabled && (mainTab === 'phenology' || mainTab === 'scouting') && (
        <div>
          <strong>{t.crop.alertsTitle}</strong>
          <div className={s.alerts}>
            {alerts.map((r: PestRecord) => (
              <MessageBar key={r.recordId} intent="warning">
                <MessageBarBody>
                  {r.name} — {pressureLabel(t, r.pressure)} ({r.affectedPct ?? '?'}%)
                </MessageBarBody>
              </MessageBar>
            ))}
          </div>
        </div>
      )}

      {/* Contenido de Fenología / Scouting */}
      {enabled && (mainTab === 'phenology' || mainTab === 'scouting') && (
        <>
          {/* ── Fenología ── */}
          {tab === 'phenology' && (
            <>
              <div className={s.toolbar}>
                <strong>{t.crop.phenoTitle}</strong>
                {modules.length > 0 && candidates.length > 1 && (
                  <Field label={t.crop.zoneFilterLabel}>
                    <Select value={moduleId} onChange={(_, d) => setModuleId(d.value)}>
                      <option value="">{t.crop.zoneAll}</option>
                      {orderedModules(modules).map(({ mod: m, depth }) => <option key={m.moduleId} value={m.moduleId}>{depth ? '↳ ' : ''}{m.name || m.moduleCode}</option>)}
                    </Select>
                  </Field>
                )}
                <Button appearance="primary" disabled={!hasAssigned} onClick={() => { setFormError(''); setPhenoDialog(true); }}>
                  {t.crop.phenoAddBtn}
                </Button>
              </div>
              {loadingPheno && <Spinner size="small" label={t.crop.loading} />}
              <DataTable<PhenologyRecord>
                rows={loadingPheno ? [] : phenology}
                rowKey={(r) => r.recordId}
                emptyMessage={loadingPheno ? '…' : t.crop.phenoNoRecords}
                columns={[
                  { key: 'date', header: t.crop.phenoTableDate, render: (r) => formatDate(r.recordedAt) },
                  { key: 'zone', header: t.crop.phenoTableZone, render: (r) => zoneName(r.moduleId) },
                  { key: 'stage', header: t.crop.phenoTableStage, render: (r) => <Badge appearance="tint">{stageLabel(t, r.stage)}</Badge> },
                  { key: 'cultivo', header: t.crop.phenoTableCrop },
                  { key: 'dap', header: t.crop.phenoTableDap, render: (r) => r.dapActual ?? '—' },
                  { key: 'by', header: t.crop.phenoTableBy, render: (r) => r.recordedByName ?? r.recordedBy },
                  ...(canWrite ? [{ key: 'actions', header: t.common.actions, width: 0.6, render: (r: PhenologyRecord) => (
                    <Button size="small" appearance="subtle" icon={<DeleteRegular />} style={{ color: tokens.colorStatusDangerForeground1 }} disabled={deletePhenoMut.isPending} onClick={() => deletePhenoMut.mutate(r.recordId)} />
                  ) }] : []),
                ]}
              />
            </>
          )}

          {/* ── Scouting ── */}
          {tab === 'scouting' && (
            <>
              <div className={s.toolbar}>
                <strong>{t.crop.scoutTitle}</strong>
                {modules.length > 0 && candidates.length > 1 && (
                  <Field label={t.crop.zoneFilterLabel}>
                    <Select value={moduleId} onChange={(_, d) => setModuleId(d.value)}>
                      <option value="">{t.crop.zoneAll}</option>
                      {orderedModules(modules).map(({ mod: m, depth }) => <option key={m.moduleId} value={m.moduleId}>{depth ? '↳ ' : ''}{m.name || m.moduleCode}</option>)}
                    </Select>
                  </Field>
                )}
                <Button appearance="primary" disabled={!hasAssigned} onClick={() => { setFormError(''); setScoutDialog(true); }}>
                  {t.crop.scoutAddBtn}
                </Button>
              </div>
              {loadingPests && <Spinner size="small" label={t.crop.loading} />}
              <DataTable<PestRecord>
                rows={loadingPests ? [] : pests}
                rowKey={(r) => r.recordId}
                emptyMessage={loadingPests ? '…' : t.crop.scoutNoRecords}
                columns={[
                  { key: 'date', header: t.crop.scoutTableDate, render: (r) => formatDate(r.recordedAt) },
                  { key: 'zone', header: t.crop.phenoTableZone, render: (r) => zoneName(r.moduleId) },
                  { key: 'name', header: t.crop.scoutTableName },
                  { key: 'type', header: t.crop.scoutTableType, render: (r) => pestLabel(t, r.pestType) },
                  { key: 'pressure', header: t.crop.scoutTablePressure, render: (r) => (
                    <Badge appearance="tint" color={r.pressure === 'CRITICO' ? 'danger' : r.pressure === 'ALTO' ? 'warning' : 'subtle'}>{pressureLabel(t, r.pressure)}</Badge>
                  ) },
                  { key: 'pct', header: t.crop.scoutTablePct, render: (r) => r.affectedPct ?? '—' },
                  { key: 'action', header: t.crop.scoutTableAction, render: (r) => r.actionTaken ?? '—' },
                  ...(canWrite ? [{ key: 'actions', header: t.common.actions, width: 0.6, render: (r: PestRecord) => (
                    <Button size="small" appearance="subtle" icon={<DeleteRegular />} style={{ color: tokens.colorStatusDangerForeground1 }} disabled={deletePestMut.isPending} onClick={() => deletePestMut.mutate(r.recordId)} />
                  ) }] : []),
                ]}
              />
              {scoutingReports.some((r: ScoutingReport) => (r.evidence?.photoUrls?.length ?? 0) > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                  <strong>{t.crop.scoutTitle} — {t.crop.scoutEvidenceTitle}</strong>
                  {scoutingReports
                    .filter((r: ScoutingReport) => (r.evidence?.photoUrls?.length ?? 0) > 0)
                    .map((r: ScoutingReport) => (
                      <div key={r.recordId}>
                        <span>{formatDate(r.recordedAt)} — {t.crop[`pest${r.pestType}` as keyof typeof t.crop] ?? r.pestType} ({t.crop[`pressure${r.pressure}` as keyof typeof t.crop] ?? r.pressure})</span>
                        <ScoutingPhotoGallery photoUrls={r.evidence!.photoUrls!} />
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Dialog fenología ── */}
      <Dialog open={phenoDialog} onOpenChange={(_, d) => { setPhenoDialog(d.open); if (!d.open) { setPhenoForm({}); fp.clear(); } }}>
        <DialogSurface>
          <DialogTitle action={
            <TelegramHelpButton
              topic="cropPhenology"
              voiceExample={t.telegramHelp.cropPhenologyVoice}
              voiceDesc={t.telegramHelp.cropPhenologyDesc}
            />
          }>{t.crop.phenoDialogTitle}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              {formError && <MessageBar intent="error"><MessageBarBody>{formError}</MessageBarBody></MessageBar>}
              <Field label={t.crop.phenoFieldCultivo} required {...fp.props('crop')}>
                {autoCrop ? (
                  <Input value={chosenCropName} readOnly aria-label={t.crop.phenoFieldCultivo} />
                ) : (
                  <Select
                    aria-label={t.crop.phenoFieldCultivo}
                    value={phenoForm.moduleId ?? ''}
                    onChange={(_, d) => { setPhenoForm(f => ({ ...f, moduleId: d.value || undefined })); fp.clear('crop'); }}
                  >
                    <option value="">—</option>
                    {scoped.map(c => <option key={c.moduleId} value={c.moduleId}>{c.label}</option>)}
                  </Select>
                )}
              </Field>
              <Field label={t.crop.phenoFieldStage} required {...fp.props('stage')}>
                <Select
                  value={phenoForm.stage ?? ''}
                  onChange={(_, d) => { setPhenoForm(f => ({ ...f, stage: d.value as FenologiaStage })); fp.clear('stage'); }}
                >
                  <option value="">—</option>
                  {STAGES.map(st => (
                    <option key={st} value={st}>{stageLabel(t, st)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.crop.phenoFieldDap}>
                <Input type="number" value={String(phenoForm.dapActual ?? '')}
                  onChange={(_, d) => setPhenoForm(f => ({ ...f, dapActual: parseInt(d.value, 10) || undefined }))} />
              </Field>
              <Field label={t.crop.phenoFieldNotes}>
                <Textarea value={phenoForm.notes ?? ''}
                  onChange={(_, d) => setPhenoForm(f => ({ ...f, notes: d.value }))} />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setPhenoDialog(false); setPhenoForm({}); fp.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutPheno.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const ok = fp.validate({ crop: !chosen && req, stage: !phenoForm.stage && req });
                if (!ok || !chosen) return;
                mutPheno.mutate({
                  ciclo,
                  ...(payloadModuleId ? { moduleId: payloadModuleId } : {}),
                  cropId:    chosen.cropId,
                  stage:     phenoForm.stage!,
                  dapActual: phenoForm.dapActual,
                  notes:     phenoForm.notes,
                });
              }}
            >{t.common.save}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* ── Dialog scouting ── */}
      <Dialog open={scoutDialog} onOpenChange={(_, d) => { setScoutDialog(d.open); if (!d.open) { setScoutForm({}); fs.clear(); } }}>
        <DialogSurface>
          <DialogTitle action={
            <TelegramHelpButton
              topic="cropPest"
              voiceExample={t.telegramHelp.cropPestVoice}
              voiceDesc={t.telegramHelp.cropPestDesc}
            />
          }>{t.crop.scoutDialogTitle}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              {formError && <MessageBar intent="error"><MessageBarBody>{formError}</MessageBarBody></MessageBar>}
              {modules.length > 0 && (
                <Field label={t.crop.phenoTableZone}>
                  <Select
                    value={scoutForm.moduleId ?? moduleId}
                    onChange={(_, d) => setScoutForm(f => ({ ...f, moduleId: d.value }))}
                  >
                    <option value="">{t.crop.zoneAll}</option>
                    {orderedModules(modules).map(({ mod: m, depth }) => <option key={m.moduleId} value={m.moduleId}>{depth ? '↳ ' : ''}{m.name || m.moduleCode}</option>)}
                  </Select>
                </Field>
              )}
              <Field label={t.crop.scoutFieldPestType} required {...fs.props('pestType')}>
                <Select
                  value={scoutForm.pestType ?? ''}
                  onChange={(_, d) => { setScoutForm(f => ({ ...f, pestType: d.value as PestType })); fs.clear('pestType'); }}
                >
                  <option value="">—</option>
                  {PEST_TYPES.map(p => (
                    <option key={p} value={p}>{pestLabel(t, p)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.crop.scoutFieldName} required {...fs.props('name')}>
                <Input value={scoutForm.name ?? ''}
                  onChange={(_, d) => { setScoutForm(f => ({ ...f, name: d.value })); fs.clear('name'); }} />
              </Field>
              <Field label={t.crop.scoutFieldPressure} required {...fs.props('pressure')}>
                <Select
                  value={scoutForm.pressure ?? ''}
                  onChange={(_, d) => { setScoutForm(f => ({ ...f, pressure: d.value as PestPressureLevel })); fs.clear('pressure'); }}
                >
                  <option value="">—</option>
                  {PRESSURES.map(p => (
                    <option key={p} value={p}>{pressureLabel(t, p)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.crop.scoutFieldPct}>
                <Input type="number" value={String(scoutForm.affectedPct ?? '')}
                  onChange={(_, d) => setScoutForm(f => ({ ...f, affectedPct: parseFloat(d.value) || undefined }))} />
              </Field>
              <Field label={t.crop.scoutFieldAction}>
                <Input value={scoutForm.actionTaken ?? ''}
                  onChange={(_, d) => setScoutForm(f => ({ ...f, actionTaken: d.value }))} />
              </Field>
              <Field label={t.crop.scoutFieldNotes}>
                <Textarea value={scoutForm.notes ?? ''}
                  onChange={(_, d) => setScoutForm(f => ({ ...f, notes: d.value }))} />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setScoutDialog(false); setScoutForm({}); fs.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutScout.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const ok = fs.validate({
                  pestType: !scoutForm.pestType && req,
                  name: !scoutForm.name?.trim() && req,
                  pressure: !scoutForm.pressure && req,
                });
                if (!ok) return;
                const scoutZone = scoutForm.moduleId ?? moduleId;
                mutScout.mutate({
                  ciclo,
                  ...(scoutZone ? { moduleId: scoutZone } : {}),
                  pestType:    scoutForm.pestType!,
                  name:        scoutForm.name!,
                  pressure:    scoutForm.pressure!,
                  affectedPct: scoutForm.affectedPct,
                  actionTaken: scoutForm.actionTaken,
                  notes:       scoutForm.notes,
                });
              }}
            >{t.common.save}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
